import { useState, useEffect, useRef } from 'react';
import {
    ExternalApplicationContainer,
    SubmittingUserStore,
} from '@servicetitan/financing-application-shared';
import { ErrorBoundary } from '@servicetitan/financing-utils-shared';
import {
    LoanApplicationSubmitSource2,
    SubmitApplicationDecision,
    FinancingApi,
    LoanStatus2,
    LoanApplicationRead,
} from '@servicetitan/financing-api-client';
import { PreApprovalActivity } from '@servicetitan/financing-api-client/src/prequals-api';
import { SelectPlanStep } from '@servicetitan/financing-application-workflow-shared';
import {
    AffirmPaymentOptionsScreen,
    useSynchronyPreApprovalActivity,
} from '@servicetitan/financing-plans-shared';
import {
    WorkflowStore,
    useProviderConfig,
    useSecondLookIntegration,
    SetupDataStore,
} from '@servicetitan/financing-workflow-shared';
import { DecisionDataStore } from '@servicetitan/financing-decision-shared';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { App } from '@servicetitan/web-feature-app-service';

const POLL_INTERVAL = 10000; // 10 seconds
const MAX_POLL_TIME = 60000 * 20; // 20 minute

export const ExternalApplicationWorkflow = () => {
    const timeoutRef = useRef<NodeJS.Timeout>();
    const queryClient = useQueryClient();

    const { submittingUser } = SubmittingUserStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { setDecisionData } = DecisionDataStore.useStore();
    const [financingApi] = useDependencies(FinancingApi);
    const providerConfig = useProviderConfig();
    const { isEnabled: isSecondLookEnabled } = useSecondLookIntegration();
    const { setupData } = SetupDataStore.useStore();
    const isAffirm = setupData?.provider?.toLowerCase() === 'affirm';

    const { log: logPreApprovalActivity, isRenderable: isSynchronyPreApproval } =
        useSynchronyPreApprovalActivity();
    // Track how the offer was resolved so its close doesn't double-log: a submission that carried the
    // offer id is AcceptApply, the not-me link is NotMeApply — either one suppresses DeclineOffer.
    const submittedRef = useRef(false);
    const notMeRef = useRef(false);

    // Check if we should skip plan selection
    const shouldSkipPlanSelection = providerConfig?.skipPlanSelection ?? false;

    const [isPlanSelected, setIsPlanSelected] = useState(shouldSkipPlanSelection);

    useEffect(() => {
        setIsPlanSelected(shouldSkipPlanSelection);
    }, [shouldSkipPlanSelection]);
    const [loanId, setLoanId] = useState<string | null>(null);
    const [shouldStopPolling, setShouldStopPolling] = useState(false);

    // Set up timeout when polling starts
    useEffect(() => {
        if (loanId && isSecondLookEnabled && !shouldStopPolling) {
            timeoutRef.current = setTimeout(() => {
                // Cancel the polling query
                queryClient.cancelQueries({ queryKey: ['loanStatus', loanId] });

                // Just stop polling, don't set decision data or change workflow stage
                setShouldStopPolling(true);
            }, MAX_POLL_TIME);

            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [loanId, isSecondLookEnabled, shouldStopPolling, queryClient]);

    // Query for polling loan status using loansGetLoanByIdOrExternalId
    const { data: loanData } = useQuery<LoanApplicationRead>({
        queryKey: ['loanStatus', loanId],
        queryFn: async () => {
            const response = await financingApi.loansGetLoanByIdOrExternalId(loanId!);
            return response.data;
        },
        enabled: !!loanId && isSecondLookEnabled && !shouldStopPolling,
        refetchInterval: POLL_INTERVAL,
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Effect to handle loan status changes
    useEffect(() => {
        if (loanData?.status && isSecondLookEnabled && !shouldStopPolling) {
            // Handle status logic first before stopping polling
            switch (loanData.status) {
                case LoanStatus2.Declined:
                    setDecisionData({
                        decision: SubmitApplicationDecision.Declined,
                        loanId: loanId!,
                        externalLoanId: loanData.externalLoanId ?? loanData.referenceNumber ?? '',
                        planNumber: null,
                        description: null,
                        planType: null,
                        approvedAmount: null,
                        term: null,
                        rate: null,
                        payment: null,
                        maxCreditAmount: null,
                        declineRedirectUrl: null,
                    });
                    setWorkflowStage('decision');
                    break;
                case LoanStatus2.Approved:
                case LoanStatus2.Prequalified:
                    // Just stop polling, don't set decision data
                    break;

                case LoanStatus2.Review:
                case LoanStatus2.AboutToSubmit:
                case LoanStatus2.Submitted:
                    // Continue polling
                    break;

                default:
                    // Handle other statuses if needed - continue polling
                    break;
            }

            // After handling the status, stop polling for final statuses
            if (
                loanData.status === LoanStatus2.Declined ||
                loanData.status === LoanStatus2.Prequalified ||
                loanData.status === LoanStatus2.Approved
            ) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                setShouldStopPolling(true);
            }
        }
    }, [
        loanData,
        loanId,
        isSecondLookEnabled,
        shouldStopPolling,
        setDecisionData,
        setWorkflowStage,
    ]);

    if (isAffirm) {
        return (
            <ErrorBoundary actionCausingError="loading Affirm payment options">
                <AffirmPaymentOptionsScreen
                    onClose={() => {
                        App.exitApp().catch(() => {
                            window.close();
                        });
                    }}
                />
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary actionCausingError="creating an application">
            {!shouldSkipPlanSelection && (
                <SelectPlanStep
                    onPlanSelected={() => {
                        submittedRef.current = false;
                        notMeRef.current = false;
                        setIsPlanSelected(true);
                    }}
                    onPreQualify={() => {
                        submittedRef.current = false;
                        notMeRef.current = true;
                        setIsPlanSelected(true);
                    }}
                    canChangeProvider
                />
            )}
            {isPlanSelected && (
                <ExternalApplicationContainer
                    onClose={hint => {
                        if (!submittedRef.current && !notMeRef.current && isSynchronyPreApproval) {
                            logPreApprovalActivity(PreApprovalActivity.DeclineOffer);
                        }
                        if (hint?.exitApplication) {
                            App.exitApp().catch(() => {
                                window.close();
                            });
                        }
                        setIsPlanSelected(false);
                    }}
                    onApplicationSubmitted={() => {
                        // Any successful submission (open OR send-link) that carried the offer is the
                        // accept — log it once, and mark submitted so the close won't also log DeclineOffer.
                        if (
                            !submittedRef.current &&
                            isSynchronyPreApproval &&
                            setupData?.preApprovalId
                        ) {
                            logPreApprovalActivity(PreApprovalActivity.AcceptApply);
                        }
                        submittedRef.current = true;
                    }}
                    postApplicationAction={id => {
                        // Open-application path only: start polling for loan status. submittedRef and
                        // AcceptApply are handled in onApplicationSubmitted (which also covers send-link).
                        if (id) {
                            setLoanId(id);
                            setShouldStopPolling(false);
                        }
                    }}
                    showTechnicianInstructions
                    allowSendViaEmail
                    submittingUser={submittingUser}
                    source={LoanApplicationSubmitSource2.Mobile}
                    skipPlanSelection={shouldSkipPlanSelection}
                />
            )}
        </ErrorBoundary>
    );
};
